import { useEffect, useRef } from 'react';

// ─────────────────────────────────────────────────────────────────────
// FOCUS FOLLOWS THE VIEW (#1144, WCAG 2.4.3 — audit D-08).
//
// The portal is one page whose views swap in place. After choosing a view
// from the menu, focus stayed on <body> (or on the menu item that is now
// gone): a screen-reader user heard nothing change, and a keyboard user had
// to Tab through the whole header again to reach what they had just opened.
//
// So on every view change — never on the first render, which is the page
// loading and owes nobody a focus move — focus goes to the new view's first
// heading inside <main>, or to <main> itself when the view has none yet (a
// view loaded on demand may not have rendered its heading in the next frame).
//
// NOTHING MOVES ON SCREEN: `preventScroll`, and the heading gets
// `tabindex="-1"` only so it can take focus — it does not join the Tab order.
// Browsers draw the focus ring for a programmatic focus only after keyboard
// use (`:focus-visible`), so a mouse user sees no change at all.
// ─────────────────────────────────────────────────────────────────────

/** Move focus to the first heading in `main`, or to `main`. */
export function focusViewHeading(main: HTMLElement | null): void {
  if (!main) return;
  const heading = main.querySelector<HTMLElement>('h1, h2');
  if (heading && !heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
  (heading ?? main).focus({ preventScroll: true });
}

/** Run `focusViewHeading` on `#main-content` after each change of `view`. */
export function useFocusOnViewChange(view: string): void {
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return undefined;
    }
    // After the new view has painted.
    const frame = requestAnimationFrame(() =>
      focusViewHeading(document.getElementById('main-content')),
    );
    return () => cancelAnimationFrame(frame);
  }, [view]);
}
